import { Col, Row, THead, Td, Th, Tr } from "@/styles/layout";
import { Body, Heading } from "@/styles/typography";
import React, { useMemo, useState } from "react";
import { useTable, Column } from "react-table";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { motion } from "framer-motion";
import { IconType } from "react-icons";
import { getCurrentTheme } from "@/styles/theme";
import Skeleton from "./Skeleton";
import { hslToHex } from "@/lib/colors";
import { useRouter } from "next/router";
import colors from "tailwindcss/colors";

interface TableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  pageSize: number;
  page?: number;
  pages?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  canNext?: boolean;
  canPrevious?: boolean;
  onRowClick?: (d: any) => void;
  isLoading?: boolean;
  skeletonHeight?: number;
  onRowHoverPath?: string;
}

export const PaginationButton = ({
  onClick,
  Icon,
  disabled,
  onHover,
}: {
  onClick: () => void;
  onHover?: () => void;
  Icon: IconType;
  disabled: boolean;
}) => {
  const containerClasses = `rounded-full hover:cursor-pointer p-1 hover:scale-95 transition-transform ${
    disabled ? "bg-gray-100" : "bg-pink-50 hover:bg-pink-100"
  }`;

  return (
    <div
      className={containerClasses}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={disabled ? undefined : onHover}
    >
      <Icon size={20} color={disabled ? colors.gray[300] : colors.pink[800]} />
    </div>
  );
};

export const ActionButton = ({
  onClick,
  Icon,
  disabled,
  color,
}: {
  onClick?: () => void;
  Icon: IconType;
  disabled?: boolean;
  color?: string;
}) => {
  return (
    <motion.div
      whileHover={{
        transform: "scale(0.9)",
      }}
      animate={{ transform: "scale(1)" }}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 56,
        pointerEvents: disabled ? "none" : "initial",
      }}
      onClick={onClick}
    >
      <Col
        p="2"
        alignItems={"center"}
        justifyContent={"center"}
        color={color ?? "primary.textHigh"}
      >
        <Icon size={14} />
      </Col>
    </motion.div>
  );
};

const SkeletonTableRows = <T extends object>({
  columns,
  count,
  height,
}: {
  columns: Column<T>[];
  count: number;
  height: number;
}) => (
  <>
    {[...Array(count).keys()].map((i) => (
      <tr key={i}>
        <td
          key={columns[0].id}
          colSpan={columns.length}
          style={{ paddingTop: 8, paddingBottom: 8 }}
        >
          <Skeleton
            delay={i * 0.02}
            height={height}
            borderRadius={4}
            color={hslToHex(getCurrentTheme().colors.primary.bgBase)}
            highlightColor={hslToHex(getCurrentTheme().colors.primary.element)}
          />
        </td>
      </tr>
    ))}
  </>
);
const MyTable = <T extends object>({
  columns,
  data,
  page,
  pages,
  pageSize,
  onNext,
  onPrevious,
  onRowHoverPath,
  canNext,
  canPrevious,
  onRowClick,
  isLoading,
  skeletonHeight = 32,
}: TableProps<T>) => {
  const router = useRouter();
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  const tableVariants = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
    hidden: {
      opacity: 0,
    },
  };

  const rowVariants = {
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.1,
      },
    },
    hidden: {
      opacity: 0,
      x: -40,
    },
  };

  return (
    <Col alignItems="stretch">
      <motion.table
        {...getTableProps({
          style: {
            borderCollapse: "collapse",
            borderRadius: 10,
            backgroundColor: getCurrentTheme().colors.primary.bgBase,
          },
        })}
        initial="hidden"
        animate="visible"
      >
        <THead>
          {headerGroups.map((headerGroup, idx) => (
            <Tr
              {...headerGroup.getHeaderGroupProps()}
              key={headerGroup.id + idx}
            >
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps({
                    style: {
                      width: column.width,
                    },
                  })}
                  key={column.id}
                  py="16px"
                  color="primary.textHigh"
                  borderLeftStyle={"solid"}
                  borderColor="primary.element"
                >
                  <Heading
                    textAlign="left"
                    fontSize="16px"
                    fontWeight="600"
                    px="16px"
                    color="primary.textLow"
                  >
                    {column.render("Header")}
                  </Heading>
                </Th>
              ))}
            </Tr>
          ))}
        </THead>
        <motion.tbody {...getTableBodyProps()} variants={tableVariants}>
          {isLoading ? (
            <SkeletonTableRows
              columns={columns}
              count={pageSize}
              height={skeletonHeight}
            />
          ) : (
            <>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <motion.tr
                    {...row.getRowProps()}
                    key={row.id}
                    variants={rowVariants}
                    onClick={() => onRowClick?.(row.original)}
                    whileHover={onRowClick ? { cursor: "pointer" } : {}}
                    onHoverStart={() => {
                      console.log("Hovering");
                      if (onRowHoverPath) {
                        if ((row.original as any).id) {
                          console.log(
                            "Prefetching " +
                              `${onRowHoverPath}/${(row.original as any).id}`
                          );
                          router.prefetch(
                            `${onRowHoverPath}/${(row.original as any).id}`
                          );
                        }
                      }
                    }}
                  >
                    {row.cells.map((cell) => (
                      <Td
                        {...cell.getCellProps({
                          style: {
                            width: cell.column.width,
                            paddingTop: 8,
                            paddingBottom: 8,
                          },
                        })}
                        key={cell.column.id}
                        borderLeftStyle={"solid"}
                        borderColor="primary.element"
                      >
                        <Body textAlign="left" fontSize="16px" px="16px">
                          {cell.render("Cell")}
                        </Body>
                      </Td>
                    ))}
                  </motion.tr>
                );
              })}
            </>
          )}
        </motion.tbody>
      </motion.table>

      {onPrevious && onNext && (pages ?? 0) > 1 && (
        <Row justifyContent={"flex-end"} mt="16px" alignItems="center">
          <Body mr="3" fontSize="14px">{`Page ${page} of ${pages}`}</Body>
          <Row>
            <PaginationButton
              onClick={onPrevious}
              Icon={GoChevronLeft}
              disabled={!canPrevious}
            />
            <PaginationButton
              onClick={onNext}
              Icon={GoChevronRight}
              disabled={!canNext}
            />
          </Row>
        </Row>
      )}
    </Col>
  );
};

export default MyTable;
